<script setup lang="ts">
import type { LicenseApplyRequirementItem } from '~/api/license-application'
import CloseIcon from '~/assets/icon/close-2.svg'

const props = withDefaults(
  defineProps<{
    modelValue?: boolean
    items?: LicenseApplyRequirementItem[]
  }>(),
  {
    modelValue: false,
    items: () => [],
  },
)

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
}>()

const { t } = useAppI18n()
const dialogTitleId = 'license-apply-requirements-title'
const showDialog = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
})

const keepHtmlAttributes = (attributes: string, names: string[]) => {
  const kept: string[] = []
  const pattern = /([:\w-]+)\s*=\s*(["'])(.*?)\2/gi
  let match: RegExpExecArray | null

  while ((match = pattern.exec(attributes))) {
    if (names.includes(match[1].toLowerCase())) kept.push(` ${match[1]}="${match[3]}"`)
  }

  return kept.join('')
}

const processReturnedHtml = (value: unknown) => {
  let content = value === null || value === undefined ? '' : String(value).trim()
  if (!content) return ''

  content = content.replace(
    /<(table|colgroup|col|tbody|tr|td|th|tfoot|thead)([^>]*)>/gi,
    (_, tag: string, attributes: string) => {
      const attributeNames: Record<string, string[]> = {
        table: ['width', 'class'],
        colgroup: ['width', 'span', 'class'],
        col: ['width', 'span', 'class'],
        tbody: ['class'],
        tr: ['class'],
        td: ['rowspan', 'colspan', 'width', 'align', 'class'],
        th: ['rowspan', 'colspan', 'width', 'align', 'class'],
        tfoot: ['class'],
        thead: ['class'],
      }

      return `<${tag}${keepHtmlAttributes(attributes, attributeNames[tag.toLowerCase()] || [])}>`
    },
  )
  content = content.replace(/<(span|p)([^>]*)>/gi, '<$1>')
  content = content.replace(/<span>\s*(?:&nbsp;|&#160;)?\s*<\/span>/gi, '')
  content = content.replace(/<p>\s*(?:(?:&nbsp;|&#160;|\s)|<br\s*\/?>|<span><\/span>)*\s*<\/p>/gi, '')
  content = content.replace(/<th\b[^>]*>[\s\S]*?<\/th>/gi, (cell) => {
    const text = cell
      .replace(/<[^>]*>/g, '')
      .replace(/&(?:nbsp|#160);/gi, '')
      .replace(/\s+/g, '')

    return text ? cell : ''
  })
  content = content.replace(/<td>\s*<\/td>/gi, '<td></td>')
  content = content.replace(/<tr\b[^>]*>[\s\S]*?<\/tr>/gi, (row) => {
    const text = row
      .replace(/<[^>]*>/g, '')
      .replace(/&(?:nbsp|#160);/gi, '')
      .replace(/\s+/g, '')

    return text ? row : ''
  })
  content = content.replace(/<table\b[^>]*>[\s\S]*?<\/table>/gi, (table) => {
    const text = table
      .replace(/<[^>]*>/g, '')
      .replace(/&(?:nbsp|#160);/gi, '')
      .replace(/\s+/g, '')

    return text ? table : ''
  })

  return content
}

/** 接口存在内容时才展示正文。 */
const requirementItems = computed(() => props.items.filter((item) => item.title || item.content))

const closeDialog = () => {
  showDialog.value = false
}
</script>

<template>
  <ClientOnly>
    <ElDialog
      v-model="showDialog"
      class="license-apply-dialog"
      modal-class="license-apply-dialog-overlay"
      width="760px"
      align-center
      :show-close="false"
      :close-on-click-modal="true"
      :close-on-press-escape="true"
      :destroy-on-close="true"
      @close="closeDialog"
    >
      <template #header>
        <header
          class="mx-[32px] flex items-center justify-between gap-[8px] border-b-[0.5px] border-solid border-[rgba(5,21,36,0.06)] pt-[32px] pb-[16px]"
        >
          <h2 :id="dialogTitleId" class="m-[0] truncate text-[20px] leading-[28px] font-[600] text-[#292a2e]">
            {{ t('84935') }}
          </h2>
          <button
            type="button"
            class="license-apply-dialog-close relative inline-flex size-[32px] cursor-pointer items-center justify-center rounded-[999px] border-[0] bg-[#f3f5f8] p-[0] transition-colors hover:opacity-80"
            aria-label="关闭牌照申请要求弹窗"
            @click="closeDialog"
          >
            <CloseIcon class="size-full" aria-hidden="true" />
          </button>
        </header>
      </template>

      <div class="max-h-[calc(100vh-128px)] overflow-x-hidden overflow-y-auto px-[32px] pt-[20px] pb-[32px]">
        <div v-if="requirementItems.length > 0" class="flex flex-col gap-[24px]">
          <section
            v-for="item in requirementItems"
            :key="`${item.title}-${item.content}`"
            class="flex flex-col gap-[8px] text-[#292a2e]"
          >
            <h3 v-if="item.title" class="m-[0] text-[18px] leading-[26px] font-[600]">
              {{ item.title }}
            </h3>
            <div
              v-if="item.content"
              class="license-apply-dialog-rich-text"
              v-html="processReturnedHtml(item.content)"
            ></div>
          </section>
        </div>
        <CommonEmptyState v-else />
      </div>
    </ElDialog>
  </ClientOnly>
</template>

<style scoped lang="less">
:global(.license-apply-dialog-overlay) {
  background: rgba(11, 18, 40, 0.36);
}

:global(.license-apply-dialog.el-dialog) {
  max-height: calc(100vh - 48px);
  max-width: calc(100vw - 48px);
  margin: auto;
  padding: 0;
  overflow: hidden;
  border-radius: 28px;
  background: #ffffff;
  box-shadow: 0 24px 80px rgba(8, 19, 62, 0.18);
}

:global(.license-apply-dialog .el-dialog__header) {
  margin: 0;
  padding: 0;
}

:global(.license-apply-dialog .el-dialog__body) {
  padding: 0;
  overflow: hidden;
}

.license-apply-dialog-rich-text {
  max-width: 100%;
  overflow-x: hidden;
  color: #292a2e;
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  white-space: pre-line;

  :deep(p),
  :deep(span) {
    margin: 0;
    color: #292a2e;
    font-size: 14px;
    line-height: 22px;
  }

  :deep(b),
  :deep(strong) {
    color: #292a2e;
    font-size: 14px;
    font-weight: 400 !important;
    line-height: 22px;
  }

  :deep(table) {
    display: block;
    width: 100%;
    max-width: 100%;
    margin-top: 24px;
    overflow-x: auto;
    overflow-y: hidden;
    border: 1px solid rgba(5, 21, 36, 0.06);
    border-collapse: separate;
    border-spacing: 0;
    table-layout: auto;
    text-align: left;
    white-space: normal;
    border-radius: 4px;
  }

  :deep(thead tr) {
    height: 54px;
    background: #f8f8f8;
  }

  :deep(tbody tr) {
    min-height: 54px;
  }

  :deep(colgroup) {
    width: 0 !important;
    height: 0 !important;
  }

  :deep(tr) {
    height: auto !important;
  }

  :deep(th),
  :deep(td) {
    min-width: 303px;
    padding: 16px;
    border-right: 1px solid rgba(5, 21, 36, 0.06);
    color: #292a2e;
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
    vertical-align: middle;
  }

  :deep(th) {
    background-color: rgba(5, 21, 36, 0.06);
  }

  :deep(th p),
  :deep(th span),
  :deep(td p),
  :deep(td span) {
    margin: 0 !important;
    font-size: 14px !important;
    line-height: 22px !important;
  }

  :deep(th:empty) {
    padding: 0;
  }

  :deep(th:last-child),
  :deep(td:last-child) {
    border-right: 0;
  }
}
</style>
